/**
 * Shared branded styles for @react-pdf/renderer based reports.
 * Matches the Monthly Task Report design language.
 *
 * Usage:
 *   import { pdfStyles, BrandedPdfHeader, BrandedPdfFooter, BrandedPdfMetaGrid } from './reactPdfStyles';
 */
import { StyleSheet } from "@react-pdf/renderer";

export const BRAND_COLORS = {
  dark: "#1D2939",
  amber: "#E6A817",
  amberLight: "#FFAF4A",
  labelGrey: "#888888",
  bodyText: "#1a1a1a",
  lightBorder: "#cccccc",
  headerBg: "#f0eeea",
  white: "#ffffff",
  muted: "#777777",
  footerGrey: "#999999",
};

export const pdfStyles = StyleSheet.create({
  /* ── Page ── */
  page: {
    padding: 28,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: BRAND_COLORS.bodyText,
    backgroundColor: BRAND_COLORS.white,
  },

  /* ── Header ── */
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: BRAND_COLORS.bodyText,
    marginBottom: 12,
  },
  brandingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  logoCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: BRAND_COLORS.amber,
    justifyContent: "center",
    alignItems: "center",
  },
  logoCircleText: {
    color: BRAND_COLORS.white,
    fontWeight: "bold",
    fontSize: 12,
  },
  logoImg: {
    width: 28,
    height: 28,
    borderRadius: 14,
    objectFit: "cover",
  },
  facilityName: {
    fontSize: 12,
    fontWeight: "bold",
    color: BRAND_COLORS.bodyText,
  },
  facilitySub: {
    fontSize: 8,
    color: BRAND_COLORS.muted,
    marginTop: 1,
  },
  reportRight: {
    textAlign: "right",
  },
  reportSub: {
    fontSize: 8,
    color: BRAND_COLORS.muted,
    marginTop: 2,
  },

  /* ── Meta grid (4-col info card) ── */
  metaGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    borderWidth: 0.5,
    borderColor: BRAND_COLORS.lightBorder,
    marginBottom: 12,
  },
  metaCell: {
    width: "25%",
    padding: "5px 8px",
    borderRightWidth: 0.5,
    borderRightColor: BRAND_COLORS.lightBorder,
  },
  metaCellLast: {
    width: "25%",
    padding: "5px 8px",
  },
  metaCellBorder: {
    width: "25%",
    padding: "5px 8px",
    borderRightWidth: 0.5,
    borderRightColor: BRAND_COLORS.lightBorder,
    borderTopWidth: 0.5,
    borderTopColor: BRAND_COLORS.lightBorder,
  },
  metaCellBorderLast: {
    width: "25%",
    padding: "5px 8px",
    borderTopWidth: 0.5,
    borderTopColor: BRAND_COLORS.lightBorder,
  },
  metaLabel: {
    fontSize: 7,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: BRAND_COLORS.labelGrey,
    marginBottom: 1,
  },
  metaValue: {
    fontSize: 9,
    fontWeight: "medium",
    color: BRAND_COLORS.bodyText,
  },

  /* ── Section header (dark bar) ── */
  sectionHeader: {
    backgroundColor: BRAND_COLORS.dark,
    padding: "5px 10px",
    marginBottom: 0,
    marginTop: 10,
  },
  sectionHeaderText: {
    color: BRAND_COLORS.white,
    fontSize: 9,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },

  /* ── Table ── */
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: BRAND_COLORS.headerBg,
    borderBottomWidth: 0.5,
    borderBottomColor: BRAND_COLORS.lightBorder,
  },
  tableHeaderCell: {
    fontSize: 7,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: BRAND_COLORS.muted,
    padding: "4px 6px",
    borderRightWidth: 0.5,
    borderRightColor: BRAND_COLORS.lightBorder,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: BRAND_COLORS.lightBorder,
  },
  tableCell: {
    fontSize: 9,
    color: BRAND_COLORS.bodyText,
    padding: "4px 6px",
    borderRightWidth: 0.5,
    borderRightColor: BRAND_COLORS.lightBorder,
  },

  /* ── Footer ── */
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 8,
    marginTop: 12,
    borderTopWidth: 0.5,
    borderTopColor: BRAND_COLORS.lightBorder,
  },
  footerText: {
    fontSize: 7,
    color: BRAND_COLORS.footerGrey,
  },
});
